import type { TFunction } from 'i18next';
import type { AvailabilityTimeEncoding } from '@ooh/contracts';
import type { TimeEncodingHelpDialogSection } from '../components/time-encoding-help-dialog';

/** One (i)-dialog section per offered `TimeEncodingSelector` option: its
 * translation-key prefix (a flat camelCase segment — react-i18next's typed
 * `t()` key-path generation doesn't reliably expand beyond ~3 dotted
 * segments, so `preview.timeEncodingHelp.<prefix>Title` is used instead of
 * a deeper `preview.timeEncodingHelp.sections.<encoding>.title`) and how
 * many example header/row cells it needs. Locale JSON also cannot hold
 * array values (see `locale-parity.spec.ts`'s `flatten()`), so each
 * section's illustrative one-row example table is stored as
 * individually-keyed `<prefix>ExampleHeader<n>` / `<prefix>ExampleRow1Cell<n>`
 * strings instead — this fixed column count reads them back into arrays. */
const SECTIONS: { encoding: AvailabilityTimeEncoding; prefix: string; columnCount: number }[] = [
  { encoding: 'global', prefix: 'global', columnCount: 3 },
  { encoding: 'date_range', prefix: 'dateRange', columnCount: 4 },
  { encoding: 'period_label_map', prefix: 'periodLabelMap', columnCount: 2 },
  { encoding: 'month', prefix: 'month', columnCount: 5 },
];

/**
 * Builds the `TimeEncodingHelpDialog`'s 4 sections from
 * `availabilityImport.json`'s `preview.timeEncodingHelp.*` keys, so both
 * locales stay the single source of truth for this illustrative content
 * (title/explanation copy AND the example table cells).
 */
export function buildTimeEncodingHelpSections(
  t: TFunction<'availabilityImport'>,
): TimeEncodingHelpDialogSection[] {
  return SECTIONS.map(({ encoding, prefix, columnCount }) => {
    const exampleFileName = t(`preview.timeEncodingHelp.${prefix}ExampleFileName`, {
      defaultValue: '',
    });
    const columns = Array.from({ length: columnCount }, (_, i) => i + 1);
    return {
      encoding,
      title: t(`preview.timeEncodingHelp.${prefix}Title`, { defaultValue: '' }),
      explanation: t(`preview.timeEncodingHelp.${prefix}Explanation`, { defaultValue: '' }),
      exampleFileName: exampleFileName || undefined,
      example: {
        headers: columns.map((n) =>
          t(`preview.timeEncodingHelp.${prefix}ExampleHeader${n}`, { defaultValue: '' }),
        ),
        rows: [
          columns.map((n) =>
            t(`preview.timeEncodingHelp.${prefix}ExampleRow1Cell${n}`, { defaultValue: '' }),
          ),
        ],
      },
    };
  });
}
